/**
 * In-memory list view state (filters, sort, page, selection).
 * Survives client-side navigation (detail → back) but resets on full page reload.
 */

import type { ApplicationQueueListSlice } from '@/lib/applicationQueueListState';

const memoryStore = new Map<string, string>();

/** Drop legacy sessionStorage list keys (pre–in-memory migration). */
function clearLegacyListSessionStorage(): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i);
      if (key?.startsWith('hiromatch:list:')) sessionStorage.removeItem(key);
    }
  } catch {
    // private mode
  }
}
clearLegacyListSessionStorage();

export function listViewStorageKey(scope: string, companyId?: string): string {
  return companyId ? `hiromatch:list:${scope}:${companyId}` : `hiromatch:list:${scope}`;
}

/**
 * Routes where the main column does not scroll — only the table body does,
 * with pagination pinned to the viewport bottom (aligned with sidebar height).
 */
export function isViewportLockedListPage(pathname: string, search = ''): boolean {
  if (pathname === '/applications' || pathname === '/job-positions') {
    return true;
  }
  // Position detail (all tabs): pin header + tab bar; scroll inside tab body / table.
  if (/^\/job-positions\/[^/]+\/?$/.test(pathname)) {
    return true;
  }
  return false;
}

/** True when the current document was loaded via browser reload (F5 / refresh). */
export function isDocumentReload(): boolean {
  if (typeof performance === 'undefined') return false;
  const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  return nav?.type === 'reload';
}

/** True when this route mount is the initial landing after F5 (not a client-side navigate). */
export function isListViewLandingAfterReload(navigationType: string): boolean {
  return navigationType === 'POP' && isDocumentReload();
}

export function loadSessionJson<T>(key: string): T | null {
  try {
    const raw = memoryStore.get(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function saveSessionJson<T>(key: string, value: T): void {
  try {
    memoryStore.set(key, JSON.stringify(value));
  } catch {
    // ignore serialization errors
  }
}

export type PersistedSort = { column: string; direction: 'asc' | 'desc' };

export type ApplicationsListPersist = {
  queueView?: 'active' | 'archive';
  statusFilter: string;
  positionFilter: string;
  teamFilter: string;
  starredFilter?: 'all' | 'starred' | 'unstarred';
  search: string;
  /** @see APPLICATIONS_LIST_SORT_VERSION — migrates legacy Applied ↓ to Updated ↓ */
  listSortVersion?: number;
  /** @deprecated Legacy flat fields — prefer `active` / `archive` slices */
  page?: number;
  pinnedStatus?: string | null;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  selectedIds?: string[];
  active?: ApplicationQueueListSlice;
  archive?: ApplicationQueueListSlice;
};

export type JobPositionsListPersist = {
  statusFilter: string;
  teamFilter: string;
  search: string;
  page: number;
  sortColumn: string;
  sortDirection: 'asc' | 'desc';
};

/** Parse `?page=` for list pagination (invalid / missing → 1). */
export function parseListPageParam(raw: string | null): number {
  if (!raw) return 1;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n >= 1 ? n : 1;
}

/** Write page into the current route search params (replace, keeps other keys). */
export function applyListPageToSearchParams(
  prev: URLSearchParams,
  page: number,
): URLSearchParams {
  const next = new URLSearchParams(prev);
  if (page <= 1) next.delete('page');
  else next.set('page', String(page));
  return next;
}


/** Write status filter into route search params (replace, keeps other keys). */
export function applyStatusToSearchParams(
  prev: URLSearchParams,
  status: string,
): URLSearchParams {
  const next = new URLSearchParams(prev);
  if (!status || status === 'all') next.delete('status');
  else next.set('status', status);
  return next;
}

export function listPageMatchesUrl(page: number, searchParams: URLSearchParams): boolean {
  return parseListPageParam(searchParams.get('page')) === page;
}

/** Passed on navigate(list → detail) so back restores filters + pagination. */
export type ApplicationListReturnState = {
  listReturnPath?: string;
};

export function buildListReturnPath(
  pathname: string,
  search: string | URLSearchParams,
  page: number,
): string {
  const params = search instanceof URLSearchParams
    ? applyListPageToSearchParams(search, page)
    : applyListPageToSearchParams(
      new URLSearchParams(search.startsWith('?') ? search.slice(1) : search),
      page,
    );
  const qs = params.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}

export type PositionApplicationsListPersist = {
  queueView?: 'active' | 'archive';
  statusFilter?: string;
  sourceFilter?: string;
  starredFilter?: 'all' | 'starred' | 'unstarred';
  search?: string;
  /** @see APPLICATIONS_LIST_SORT_VERSION — migrates legacy Applied ↓ to Updated ↓ */
  listSortVersion?: number;
  /** @deprecated Legacy flat fields — prefer `active` / `archive` slices */
  page?: number;
  pinnedStatus?: string | null;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  selectedIds?: string[];
  active?: ApplicationQueueListSlice;
  archive?: ApplicationQueueListSlice;
};
